import React, { ReactNode, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TextButton } from '~/components/shared/TextButton'
import { Tooltip } from '~/components/ui/tooltip'
import { useDebounce } from '~/hooks/useDebounce'
import { useOutsideClick } from '~/hooks/useOutsideClick'
import { hasValue } from '~/utils/common'
import { castRef } from '~/utils/refUtils'

import { TextButtonVariants } from '../componentStyles/TextButton.style'
import { SearchInput } from '../SearchInput'
import { Menu, MenuOptions, MenuPositioningWrapper } from './Menu'
import { MenuItem } from './MenuItem'

const SEARCH_DEBOUNCE_RATE_MS = 300

export type TextButtonMenuItem<TValue> = MenuItem<TValue> & {
  buttonLabel?: string
}

type SearchableDropdownTextButtonProps<TValue> = {
  children?: ReactNode
  tooltipLabel?: string
  leftIcon?: SVGComponent
  rightIcon?: SVGComponent
  onButtonClick?: () => void
  items: TextButtonMenuItem<TValue>[]
  defaultItem?: TextButtonMenuItem<TValue>
  onSelect?: (value: TValue, index: number) => string | void
  onMenuOpenChange?: (isOpen: boolean) => boolean | undefined
  onScrollEndReached?: () => void
  onSearchChanged?: (searchValue: string) => void
  menuOptions?: MenuOptions
  placeholder?: string
  variant?: TextButtonVariants
}

export const SearchableDropdownTextButton = <
  TValue extends string | number | object | never,
>({
  children,
  placeholder,
  tooltipLabel,
  leftIcon,
  rightIcon,
  onButtonClick,
  items,
  defaultItem,
  onSelect,
  onMenuOpenChange,
  onScrollEndReached,
  onSearchChanged,
  menuOptions,
  variant = TextButtonVariants.Gray_900,
}: SearchableDropdownTextButtonProps<TValue>) => {
  const { t } = useTranslation()
  const [selectedItem, setSelectedItem] = useState<
    TextButtonMenuItem<TValue> | undefined
  >(defaultItem)
  const [menuOpen, setMenuOpen] = useState(false)
  const [unformattedInputValue, setUnformattedInputValue] = useState<string>('')
  const [searchValue, setSearchValue] = useState({ term: '' })

  const containerRef = React.useRef<HTMLDivElement>(null)
  const buttonRef = React.useRef<HTMLDivElement>(null)

  const [, cancelDebounce] = useDebounce(
    () => {
      const trimmed = unformattedInputValue.trim()
      setSearchValue({ term: trimmed })
    },
    SEARCH_DEBOUNCE_RATE_MS,
    [unformattedInputValue],
  )

  useEffect(() => {
    if (items.length > 0 && !selectedItem) {
      setSelectedItem(items[0])
      onSelect?.(items[0].value, 0)
    }
  }, [items])

  //on mount, don't update search value
  useEffect(() => {
    cancelDebounce()

    return () => {
      cancelDebounce()
    }
  }, [])

  useEffect(() => {
    onSearchChanged?.(searchValue.term)
  }, [searchValue])

  // Clear the search when the menu closes so reopening starts from the full,
  // unfiltered list rather than a stale term (the SearchInput unmounts with the
  // menu and resets visually, but this state would otherwise persist).
  useEffect(() => {
    if (
      !menuOpen &&
      (unformattedInputValue !== '' || searchValue.term !== '')
    ) {
      cancelDebounce()
      setUnformattedInputValue('')
      setSearchValue({ term: '' })
    }
  }, [menuOpen])

  useOutsideClick({
    ref: castRef(containerRef),
    handler: () => {
      if (menuOpen) {
        setMenuOpen(false)
        onMenuOpenChange?.(false)
      }
    },
  })

  const buttonLabel = useMemo(() => {
    return (
      selectedItem?.buttonLabel ??
      selectedItem?.content ??
      children ??
      placeholder
    )
  }, [items, selectedItem])

  const handleButtonClick = () => {
    const didMenuOpenChangeCancel = onMenuOpenChange?.(!menuOpen) === false
    if (!menuOpen && didMenuOpenChangeCancel) {
      // The handler returned false, cancelling the open action
      return
    }

    setMenuOpen((prev) => !prev)
    onMenuOpenChange?.(!menuOpen)
    onButtonClick?.()
  }
  const handleSelect = (value: TValue, index: number) => {
    onSelect?.(value, index)
    setSelectedItem(items[index])
    setMenuOpen(false)
    onMenuOpenChange?.(false)
  }

  return (
    <MenuPositioningWrapper ref={containerRef} marginLeft={-5}>
      <Tooltip
        showArrow
        content={tooltipLabel}
        positioning={{ placement: 'top' }}
        disabled={!hasValue(tooltipLabel)}
      >
        <TextButton
          ref={buttonRef}
          leftIcon={leftIcon}
          rightIcon={rightIcon}
          onLinkClick={handleButtonClick}
          iconSize="0.75rem"
          variant={variant}
        >
          {buttonLabel}
        </TextButton>
      </Tooltip>
      {menuOpen && (
        <Menu
          anchorRef={castRef(buttonRef)}
          items={items}
          onSelect={handleSelect}
          options={menuOptions}
          onScrollEndReached={onScrollEndReached}
          header={
            <SearchInput
              placeholder={placeholder ?? t('form.placeholder.searchNumbers')}
              onChange={(searchValue) => {
                setUnformattedInputValue(searchValue.value)
              }}
            />
          }
          headerWrapperVariant="divided"
        />
      )}
    </MenuPositioningWrapper>
  )
}
